interface SimplePricingBreakdownProps {
  breakdown: {
    apiRateCents?: number;
    baseRateCents?: number;
    weightSurchargeCents?: number;
    pickupFeeCents?: number;
    deliveryFeeCents?: number;
    platformFeeCents?: number;
    insuranceCents?: number;
    packagingCents?: number;
    signatureCents?: number;
    propertyTypeSurchargeCents?: number;
    subtotalCents: number;
    taxCents: number;
    totalCents: number;
    actualWeightGrams?: number;
    dimensionalWeightGrams?: number;
    chargeableWeightGrams?: number;
  };
  className?: string;
}

const formatPrice = (cents: number) => {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
};

export function SimplePricingBreakdown({
  breakdown,
  className = "",
}: SimplePricingBreakdownProps) {
  return (
    <div className={className}>
      <h4 className="font-medium mb-3">Pricing Breakdown</h4>
      <div className="space-y-1 min-w-[300px] max-w-md border rounded-lg p-3 bg-gray-50">
        {/* API Rate for external carriers */}
        {breakdown.apiRateCents !== undefined &&
          breakdown.apiRateCents !== null &&
          breakdown.apiRateCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Carrier Rate</span>
              <span>{formatPrice(breakdown.apiRateCents)}</span>
            </div>
          )}
        {/* Base Rate for local carriers */}
        {(!breakdown.apiRateCents || breakdown.apiRateCents === 0) &&
          breakdown.baseRateCents !== undefined &&
          breakdown.baseRateCents !== null &&
          breakdown.baseRateCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Base Rate</span>
              <span>{formatPrice(breakdown.baseRateCents)}</span>
            </div>
          )}
        {breakdown.weightSurchargeCents !== undefined &&
          breakdown.weightSurchargeCents !== null &&
          breakdown.weightSurchargeCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Weight Surcharge</span>
              <span>{formatPrice(breakdown.weightSurchargeCents)}</span>
            </div>
          )}
        {breakdown.pickupFeeCents !== undefined &&
          breakdown.pickupFeeCents !== null &&
          breakdown.pickupFeeCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Pickup Fee</span>
              <span>{formatPrice(breakdown.pickupFeeCents)}</span>
            </div>
          )}
        {breakdown.deliveryFeeCents !== undefined &&
          breakdown.deliveryFeeCents !== null &&
          breakdown.deliveryFeeCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">
                {breakdown.apiRateCents ? "Drop-off Fee" : "Delivery Fee"}
              </span>
              <span>{formatPrice(breakdown.deliveryFeeCents)}</span>
            </div>
          )}
        {breakdown.platformFeeCents !== undefined &&
          breakdown.platformFeeCents !== null &&
          breakdown.platformFeeCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Service Fee</span>
              <span>{formatPrice(breakdown.platformFeeCents)}</span>
            </div>
          )}
        {breakdown.insuranceCents !== undefined &&
          breakdown.insuranceCents !== null &&
          breakdown.insuranceCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Insurance</span>
              <span>{formatPrice(breakdown.insuranceCents)}</span>
            </div>
          )}
        {breakdown.packagingCents !== undefined &&
          breakdown.packagingCents !== null &&
          breakdown.packagingCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Packaging</span>
              <span>{formatPrice(breakdown.packagingCents)}</span>
            </div>
          )}
        {breakdown.signatureCents !== undefined &&
          breakdown.signatureCents !== null &&
          breakdown.signatureCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Signature</span>
              <span>{formatPrice(breakdown.signatureCents)}</span>
            </div>
          )}
        {breakdown.propertyTypeSurchargeCents !== undefined &&
          breakdown.propertyTypeSurchargeCents !== null &&
          breakdown.propertyTypeSurchargeCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Property Surcharge</span>
              <span>{formatPrice(breakdown.propertyTypeSurchargeCents)}</span>
            </div>
          )}
        <div className="border-t pt-1" />
        <div className="flex justify-between text-xs">
          <span className="text-gray-600">Subtotal</span>
          <span>{formatPrice(breakdown.subtotalCents)}</span>
        </div>
        {breakdown.taxCents !== undefined &&
          breakdown.taxCents !== null &&
          breakdown.taxCents > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Tax (HST 13%)</span>
              <span>{formatPrice(breakdown.taxCents)}</span>
            </div>
          )}
        <div className="border-t pt-1" />
        <div className="flex justify-between text-xs font-semibold">
          <span>Total</span>
          <span>{formatPrice(breakdown.totalCents)}</span>
        </div>
        {/* Weight information */}
        {breakdown.chargeableWeightGrams &&
          breakdown.chargeableWeightGrams > 0 && (
            <div className="border-t pt-2 mt-2">
              <div className="text-xs text-gray-500">
                <div>
                  Actual Weight:{" "}
                  {((breakdown.actualWeightGrams ?? 0) / 1000).toFixed(2)} kg
                </div>
                <div>
                  Dimensional Weight:{" "}
                  {((breakdown.dimensionalWeightGrams ?? 0) / 1000).toFixed(2)}{" "}
                  kg
                </div>
                <div>
                  Chargeable Weight:{" "}
                  {(breakdown.chargeableWeightGrams / 1000).toFixed(2)} kg
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
